import React from "react";

/**
 * The five KPI cards above the Mieterübersicht.
 *
 * Every card reads its own total from its own source — none of them counts
 * the rows on screen. That is the whole point of the component boundary: a
 * page that renders 25 tenancies must never be able to report "25" as a
 * total, and keeping the numbers out of the row pipeline makes that
 * structurally impossible rather than merely intended.
 *
 * The two tenancy cards come from ONE summary, so "Aktiv" and "Storniert"
 * cannot drift apart — but they read DIFFERENT fields of it, because they ask
 * different questions.
 *
 * "Storniert" is a status bucket: `display_status_counts.cancelled`.
 *
 * "Aktive Mietverhältnisse" is not. It reads `summary.running`, the count of
 * contracts live on the Swiss business day, derived on the server by
 * `tenancy_lifecycle.tenancy_is_running_on`. It is deliberately neither
 * `display_status_counts.active` (which drops a tenancy the day its notice is
 * recorded, though it runs for months more) nor `active + notice_given` (which
 * would keep counting a termination whose effective date passed years ago and
 * whose move-out nobody ever recorded — that row still derives `notice_given`).
 * Neither sum is computed here: this component never adds status buckets and
 * never compares a date. One field, one meaning, decided once on the server.
 *
 * Each card carries its own loading and error state. A failed summary leaves
 * the tenant count working and says so on the two it feeds, instead of taking
 * the page down.
 *
 * Colour is a low-opacity tint plus a saturated label, which is what the
 * reference layout does and what survives both themes: the tint sits over
 * `bg-card` and the text takes a `dark:` sibling for contrast. No card
 * hardcodes a background or a foreground.
 */

const TONES = {
  blue: {
    shell: "border-sky-500/25 bg-sky-500/[0.07]",
    accent: "text-sky-700 dark:text-sky-300",
  },
  green: {
    shell: "border-emerald-500/25 bg-emerald-500/[0.07]",
    accent: "text-emerald-700 dark:text-emerald-300",
  },
  amber: {
    shell: "border-amber-500/25 bg-amber-500/[0.07]",
    accent: "text-amber-700 dark:text-amber-300",
  },
  rose: {
    shell: "border-rose-500/25 bg-rose-500/[0.07]",
    accent: "text-rose-700 dark:text-rose-300",
  },
  neutral: {
    shell: "border-border bg-muted/40",
    accent: "text-muted-foreground",
  },
};

function StatCard({ label, value, hint, tone = "neutral", title, testId }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <div
      className={`rounded-[10px] border p-[14px_16px] ${t.shell}`}
      data-testid={testId}
      title={title || undefined}
    >
      <p className={`m-0 text-[11px] font-semibold leading-tight ${t.accent}`}>
        {label}
      </p>
      <p
        className={`m-0 mt-[6px] text-[28px] font-semibold leading-none tabular-nums ${t.accent}`}
      >
        {value}
      </p>
      <p className="m-0 mt-[6px] text-[11px] leading-tight text-muted-foreground">
        {hint}
      </p>
    </div>
  );
}

/** Resolve a `{loading, error, value}` slot into what the card should show. */
function resolve(state, hint, pick) {
  if (state?.loading) return { value: "…", hint: "Wird geladen …" };
  if (state?.error) return { value: "—", hint: "Konnte nicht geladen werden." };
  const picked = pick ? pick(state?.value) : state?.value;
  if (picked == null) return { value: "—", hint };
  return { value: String(picked), hint };
}

/** One derived-status count out of the summary payload. */
function countOf(summary, key) {
  const counts = summary?.display_status_counts;
  if (!counts || typeof counts !== "object") return null;
  const n = counts[key];
  return typeof n === "number" ? n : null;
}

/** The server's canonical count of contracts running today. */
function runningCount(summary) {
  const n = summary?.running;
  return typeof n === "number" ? n : null;
}

export default function TenantOverviewStats({ tenantsTotal, statusSummary }) {
  const tenants = resolve(tenantsTotal, "Alle erfassten Mieter");
  const active = resolve(statusSummary, "Laufende Verträge", runningCount);
  const cancelled = resolve(statusSummary, "Stornierte Verträge", (v) =>
    countOf(v, "cancelled")
  );

  return (
    <div
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      data-testid="tenant-overview-stats"
    >
      <StatCard
        testId="kpi-tenants-total"
        tone="blue"
        label="Mieter gesamt"
        value={tenants.value}
        hint={tenants.hint}
      />
      <StatCard
        testId="kpi-active-tenancies"
        tone="green"
        label="Aktive Mietverhältnisse"
        value={active.value}
        hint={active.hint}
      />
      {/* Deliberately unpopulated. The rental-contract lifecycle never writes
          `signed`, so any "pending" count would be every contract that is not
          cancelled — a number that looks authoritative and means nothing. */}
      <StatCard
        testId="kpi-pending-contracts"
        tone="neutral"
        label="Ausstehende Verträge"
        value="—"
        hint="Noch nicht verfügbar"
        title="Der Vertragsstatus wird derzeit noch nicht vollständig erfasst."
      />
      {/* Also deliberately unpopulated, and this one used to show a number.
          `GET /api/admin/invoice-summary` reads the native `invoices` table
          only, so it is blind to the Bexio mirror; it counts cancelled
          invoices as open (`status != 'paid'`); and its count and its amount
          cover different sets. Adding the two worlds together here is exactly
          what the invoice-retirement work exists to do correctly — historic
          rent can sit in both as the same claim, so a naive sum double-counts.
          Until that slice publishes a source-agnostic admin aggregate, an em
          dash is the honest value. */}
      <StatCard
        testId="kpi-open-invoices"
        tone="neutral"
        label="Offene Rechnungen"
        value="—"
        hint="Noch nicht verfügbar"
        title="Wird nach Abschluss der Rechnungszusammenführung verfügbar."
      />
      <StatCard
        testId="kpi-cancelled-tenancies"
        tone="rose"
        label="Stornierte Mietverhältnisse"
        value={cancelled.value}
        hint={cancelled.hint}
      />
    </div>
  );
}
